// Client for pagos-svc (SPEI payouts via Fintoc).
//
// Deliberately intent-based: every call names a PAYABLE, never an amount, a
// CLABE or a beneficiary. The server derives those from the database itself.
// If you ever find yourself wanting to add an `amount` parameter here, that is
// the bug — see services/pagos-svc/README.md, "Rule zero".
//
// Fintoc is in sandbox. This runs ALONGSIDE the existing payment flow (the
// Banco BASE dispersion file and the manual comprobante upload), which remain
// the production path. Nothing here replaces them.
import supabase from "../supabase-client";

const BASE = (process.env.REACT_APP_PAGOS_URL || "").replace(/\/$/, "");

// When the service is not configured the UI hides every SPEI affordance, so a
// missing env var degrades to exactly the behaviour that existed before.
export const speiEnabled = () => !!BASE;

async function authHeaders() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new PagosError("sin_sesion", "Tu sesión expiró. Vuelve a iniciar sesión.");
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

export class PagosError extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
  }
}

// Server error codes → what a person should actually read.
const MESSAGES = {
  sin_sesion: "Tu sesión expiró. Vuelve a iniciar sesión.",
  forbidden: "No tienes el permiso services.pay para pagar servicios.",
  service_not_found: "No se encontró el servicio.",
  bad_status: "El servicio no está aprobado para pago.",
  unverified_clabe: "La CLABE del proveedor no está verificada.",
  clabe_drift: "La CLABE del proveedor cambió desde su verificación. Hay que verificarla de nuevo.",
  amount_drift: "El saldo del servicio cambió. Vuelve a abrir la ventana.",
  nothing_owed: "El servicio no tiene saldo pendiente.",
  already_in_flight: "Ya hay un pago en curso para este servicio.",
  no_items: "No seleccionaste nada que pagar.",
  invalid_comment: "El concepto solo admite letras, números, espacios y guiones (máximo 40).",
  reason_required: "Escribe el motivo (mínimo 10 caracteres).",
  override_failed: "No se pudo autorizar la cuenta.",
  insufficient_balance: "Saldo insuficiente en la cuenta de pagos. Fondea la cuenta antes de pagar.",
  // deposit returns
  not_fintoc_deposit: "Este depósito no llegó por SPEI a Fintoc, así que no se puede devolver automáticamente.",
  already_returned: "El depósito de esta reservación ya fue devuelto.",
  already_sent: "Ya se envió una devolución para esta reservación.",
  nothing_to_return: "No hay depósito por devolver.",
  exceeds_deposit: "Se intenta devolver más de lo que entró por Fintoc.",
  no_origin_account: "No se conoce la cuenta de origen del depósito.",
  not_found: "No se encontró la reservación.",
  // Clip card refunds
  not_clip_deposit: "Este depósito no se cobró por Clip.",
  spans_charges: "El depósito se cobró en varios cargos Clip; devuélvelo manualmente.",
  refund_window_expired: "Pasaron más de 180 días desde el cobro; Clip ya no lo reembolsa.",
  clip_not_configured: "Clip no está configurado en el servicio de pagos.",
  note_required: "Escribe qué se verificó en Clip.",
  // statements
  bad_range: "Revisa las fechas del periodo.",
  range_too_long: "El periodo máximo es de un año.",
  bad_account: "Cuenta desconocida.",
};

// The SPEI concepto as pagos-svc accepts it (concept.js): accents folded,
// then only letters, digits, spaces and hyphens, at most 40.
export const foldConcept = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim();
export const conceptError = (s) => {
  const f = foldConcept(s);
  if (!f) return "Escribe el concepto";
  if (f.length > 40) return "Máximo 40 caracteres";
  return /^[A-Za-z0-9 -]+$/.test(f) ? null : "Solo letras, números, espacios y guiones";
};

export const messageFor = (code, fallback) =>
  MESSAGES[code] || fallback || "Ocurrió un error al contactar el servicio de pagos.";

async function request(path, options = {}) {
  let res;
  try {
    res = await fetch(`${BASE}${path}`, { ...options, headers: await authHeaders() });
  } catch (err) {
    // Network-level failure: the service is down or unreachable. Never retried
    // automatically here — a retry of an authorization is a second payout.
    throw new PagosError("sin_conexion", "No se pudo contactar al servicio de pagos.");
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok && res.status !== 207) {
    throw new PagosError(body.error || "error", messageFor(body.error, body.message));
  }
  return body;
}

// What WOULD be paid, computed server-side. This is what the confirmation
// dialog shows — never a number the browser worked out for itself.
export function previewPayable(kind, id) {
  return request(`/api/payables/${kind}/${encodeURIComponent(id)}`);
}

// One human gesture, N independent payouts. Each item gets its own row, its
// own idempotency key and its own outcome; one bad row cannot fail the rest.
export function authorizePayouts(items) {
  return request("/api/payouts", { method: "POST", body: JSON.stringify({ items }) });
}

export function getPayout(payoutId) {
  return request(`/api/payouts/${encodeURIComponent(payoutId)}`);
}

// Follow a payout to a terminal state, reporting each stage as it changes.
//
// A payout is not instant: it goes queued -> submitted -> succeeded as the
// worker picks it up, signs the transfer and the webhook lands. Live SPEI has
// been taking ~13s end to end. Without this the UI has nothing truthful to
// show between "authorised" and "done".
// 'awaiting_funds' is terminal FOR THE UI, not for the payout: the money is
// not there yet, the authorization stands, and the next top-up sends it. There
// is nothing to wait for on screen, so stop polling and say so.
const TERMINAL = ["succeeded", "failed", "returned", "canceled", "awaiting_funds"];

export async function waitForPayout(payoutId, onStage, opts = {}) {
  const { timeoutMs = 90000, intervalMs = 2000 } = opts;
  const started = Date.now();
  let lastStatus = null;

  while (Date.now() - started < timeoutMs) {
    let payout;
    try {
      payout = await getPayout(payoutId);
    } catch {
      // A blip polling status must never be reported as a payment failure —
      // the money may well be on its way. Keep trying until the timeout.
      await new Promise((r) => setTimeout(r, intervalMs));
      continue;
    }
    if (payout.status !== lastStatus) {
      lastStatus = payout.status;
      onStage?.(payout.status, payout);
    }
    if (TERMINAL.includes(payout.status)) return payout;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  // Timeout is NOT failure. The transfer is very likely still in flight, and
  // claiming otherwise would invite someone to pay twice.
  return { status: "pending_timeout" };
}

// Fintoc's branded comprobante PDF. The URL is signed and short-lived, so it is
// minted per click and never stored.
export async function getPayoutReceipt(payoutId) {
  return request(`/api/payouts/${encodeURIComponent(payoutId)}/receipt`);
}

// Live balance of the account payouts are sent from. Never cached: the whole
// point is to reflect what is there right now.
export function getFunds() {
  return request("/api/funds");
}

// Manually accept an account the banking system disagreed with. Requires
// services.pay and a written reason; the bank's own verdict is never rewritten.
// documentPath (egresos-docs) is optional evidence when the bank said the
// account does not exist — only overridable for a tercero's STP (646…) CLABE.
export function overrideVerification(ownerType, ownerId, reason, documentPath = null) {
  return request("/api/verifications/override", {
    method: "POST",
    body: JSON.stringify({ owner_type: ownerType, owner_id: ownerId, reason, document_path: documentPath }),
  });
}

// Estado de cuenta of one Fintoc account (general | cobranza | servicios |
// depositos | traslados), `from`/`to` as YYYY-MM-DD Monterrey days. Fintoc's own
// movements and balances; annotate with utility/fintocStatement.js.
export function getStatement(account, from, to) {
  const qs = new URLSearchParams({ from, to });
  return request(`/api/statements/${encodeURIComponent(account)}?${qs}`);
}

// --- Clip card refunds (deposit returns to the card that paid) -------------
//
// Same rule as payouts: the browser names a reservation, never an amount or a
// card. pagos-svc re-derives both from Clip's record of the charge.

export function previewClipRefund(reservationId) {
  return request(`/api/clip-refunds/preview/${encodeURIComponent(reservationId)}`);
}

export function requestClipRefund(reservationId) {
  return request("/api/clip-refunds", {
    method: "POST",
    body: JSON.stringify({ reservation_id: Number(reservationId) }),
  });
}

export function getClipRefund(id) {
  return request(`/api/clip-refunds/${encodeURIComponent(id)}`);
}

// outcome: "refunded" | "not_refunded" | "cancel". Requires services.pay.
export function resolveClipRefund(id, outcome, note) {
  return request(`/api/clip-refunds/${encodeURIComponent(id)}/resolve`, {
    method: "POST",
    body: JSON.stringify({ outcome, note }),
  });
}

// Follow a refund until Clip answers. 'awaiting_funds' is terminal for the UI:
// it sends itself once the day's Clip sales cover it.
const CLIP_TERMINAL = ["succeeded", "failed", "unknown", "awaiting_funds", "canceled"];

export async function waitForClipRefund(id, opts = {}) {
  const { timeoutMs = 60000, intervalMs = 2000 } = opts;
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    try {
      const row = await getClipRefund(id);
      if (CLIP_TERMINAL.includes(row.status)) return row;
    } catch {
      /* a polling blip is not a refund failure */
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return { status: "pending_timeout" };
}
